<template>
  <div>
    <AppDialog
      title="Configure SSO"
      :show="showDialog"
      :loading="loading"
      size="large"
      append-to-body
      position="centered"
      @close="cancelDialog"
    >
      <AppWizard
        id="sso-setup-wizard"
        :value="step"
        :steps="wizardSteps"
      >
        <ValidationObserver ref="dialogObserver">
          <!-- IdentityProviderStep -->
          <AppWizardStep
            v-if="step === IDENTITY_PROVIDER_STEP"
            :show-back-button="false"
            :show-delete-button="false"
            @previous="previousStep"
            @next="nextStep"
            @tab-selected="changeTab"
            @close="cancelDialog"
          >
            <ValidationObserver ref="IdentityProviderStepObserver">
              <IdentityProviderStep
                :sso="localSso"
                :sso-sp-acs-url="sso_sp_acs_url"
                :sso-sp-entity-id="sso_sp_entity_id"
              />
              <ProgressNotice />
            </ValidationObserver>
          </AppWizardStep>

          <!-- ConfigureSamlStep -->
          <AppWizardStep
            v-if="step === CONFIGURE_SAML_STEP"
            show-back-button
            :show-delete-button="false"
            is-last-step
            save-button-text="Verify & finish"
            @input="nextStep"
            @previous="previousStep"
            @tab-selected="changeTab"
            @close="cancelDialog"
          >
            <ValidationObserver ref="ConfigureSamlStepObserver">
              <ConfigureSamlStep :sso="localSso" />
              <ProgressNotice />
            </ValidationObserver>
          </AppWizardStep>
        </ValidationObserver>
      </AppWizard>
    </AppDialog>
  </div>
</template>

<script>
import ConfigureSamlStep from '~/components/ssos/ConfigureSamlStep.vue';
import IdentityProviderStep from '~/components/ssos/IdentityProviderStep.vue';
import ProgressNotice from '~/components/ssos/ProgressNotice.vue';
import { SSOResource } from '~/resources';

const IDENTITY_PROVIDER_STEP = 0;
const CONFIGURE_SAML_STEP = 1;

export default {
  name: 'SSOWizard',
  components: {
    ProgressNotice,
    IdentityProviderStep,
    ConfigureSamlStep,
  },
  mixins: [],
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
    sso: {
      type: Object,
      required: true,
      default: () => ({
        hubstaff_assertion_consumer_service_url: '',
        hubstaff_issuer: '',
        domains: '',
        errors: {},
        entity_id: '',
        full_certificate: '',
        sso_url: '',
      }),
    },
    sso_sp_acs_url: {
      type: String,
      required: true,
    },
    sso_sp_entity_id: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: IDENTITY_PROVIDER_STEP,
      showDialog: this.show,
      loading: false,
      userIdpLoginUrl: '',
      IDENTITY_PROVIDER_STEP,
      CONFIGURE_SAML_STEP,
      localSso: { ...this.sso },
    };
  },
  computed: {
    wizardSteps() {
      return [
        { title: 'Set up hubstaff in your IdP' }, // Step 1
        { title: 'Configure SAML' }, // Step 2
      ];
    },
  },
  watch: {
    sso: {
      handler(newVal) {
        this.localSso = { ...newVal };
      },
      immediate: true,
    },
  },
  mounted() {
    this.displaySsoVerified();
  },
  methods: {
    displaySsoVerified() {
      const urlParams = new URLSearchParams(window.location.search);

      if (!urlParams.has('sso_verified')) {
        return;
      }
      const ssoVerified = urlParams.get('sso_verified');

      if (ssoVerified === 'true') {
        this.$appMessage('SSO is now configured and available for your organization members', {
          header: 'Configuration successful',
        });
      } else if (ssoVerified === 'false') {
        this.$appMessage('SSO configuration could not be verified. Please check your settings.');
      }

      urlParams.delete('sso_verified');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;

      window.history.replaceState({}, '', newUrl);
    },
    async loadInitialData() {
      this.showDialog = true;
    },
    closeAllDialogs() {
      this.showDialog = false;
      this.$emit('close');

      if (this.$refs.dialogObserver) {
        this.$refs.dialogObserver.reset();
      }

      this.resetSSOfields();
      this.step = IDENTITY_PROVIDER_STEP;
      this.loading = false;
    },
    async cancelDialog() {
      this.closeAllDialogs();
    },
    currentObserver() {
      return {
        [CONFIGURE_SAML_STEP]: this.$refs.ConfigureSamlStepObserver,
        [IDENTITY_PROVIDER_STEP]: this.$refs.IdentityProviderStepObserver,
      }[this.step];
    },
    previousStep() {
      this.step--;
    },
    resetSSOfields() {
      this.localSso = { ...this.sso };
    },
    async nextStep() {
      const isValid = await this.currentObserver().validate();

      if (!isValid) {
        return;
      }
      let keepLoading;
      const stepHandlers = {
        [CONFIGURE_SAML_STEP]: async () => {
          try {
            this.loading = true;
            const params = { organizationId: this.organizationId };
            const response = await SSOResource.create(params, { sso: this.localSso }, { abortReload: true });

            if (response.verification_url) {
              window.location.href = response.verification_url;
              keepLoading = true;

              return false;
            }

            if (response.errors) {
              this.$refs.dialogObserver.setErrors(response.errors);
              this.$appMessage('Validation failed. Please check your inputs.');

              return false;
            }

            this.$appMessage('Unexpected response. Please try again.');

            return false;
          } catch (error) {
            console.error('Error creating SSO configuration:', error);

            if (error.responseJSON?.errors) {
              this.$refs.dialogObserver.setErrors(error.responseJSON.errors);
              this.$appMessage('Validation failed. Please check your inputs.');
            } else if (![401, 403].includes(error.status)) {
              this.$appMessage('Unhandled error. Please try again.');
            }

            return false;
          } finally {
            if (!keepLoading) {
              this.loading = false;
            }
          }
        },
      };
      const handler = stepHandlers[this.step];
      const shouldAdvance = handler ? await handler() : true;

      if (shouldAdvance) {
        this.step++;
        this.$refs.ConfigureSamlStepObserver?.reset();
      }
    },
    changeTab(tabId) {
      this.step = Number(tabId.replace('tab-', ''));
    },
  },
};
</script>
